import React, { Fragment, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  useAPI,
  Button,
  Image,
  Loader,
  ViewContext,
  AuthContext,
} from "components/lib";
import Axios from "axios";
import { StylesManager, Model, QuestionTagboxModel } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
import { applyStackgoTheme } from "components/survey/stackgo-theme";

StylesManager.applyTheme("defaultV2");

export function HubSpotSettingsView(props) {
  const hubspotFieldsResponse = useAPI("/api/integration/hubspot/fields");
  const exstingSettingsResponse = useAPI("/api/integration/hubspot/settings");
  const hubspotConnected = useAPI("/api/integration/hubspot/connected");
  const accountResponse = useAPI("/api/account");
  const [dateFields, setDateFields] = useState([]);
  const [textFields, setTextFields] = useState([]);
  const [phoneFields, setPhoneFields] = useState([]);
  const [selectFields, setSelectFields] = useState([]);

  const env = process.env.NODE_ENV;
  const navigateToUrl =
    env === "production"
      ? `https://identity.stackgo.io/account`
      : `http://localhost:3000/account`;

  useEffect(() => {
    console.log("hubspotFieldsResponse", hubspotFieldsResponse);
    if (
      hubspotFieldsResponse.loading === false &&
      hubspotFieldsResponse.data !== null
    ) {
      const dateHSFields = hubspotFieldsResponse?.data?.results?.filter(
        (f) =>
          f.fieldType === "date" &&
          f?.modificationMetadata?.readOnlyValue === false
      );
      const textHSFields = hubspotFieldsResponse?.data?.results?.filter(
        (f) =>
          f.fieldType === "text" &&
          f?.modificationMetadata?.readOnlyValue === false
      );

      const dateChoices = dateHSFields.map((field) => {
        return {
          value: field.name,
          text: field.label,
        };
      });

      const textChoices = textHSFields.map((field) => {
        return {
          value: field.name,
          text: field.label,
        };
      });

      // Phone number properties are fieldType "phonenumber" (e.g. Phone,
      // Mobile phone number), not "text" — include both so the SMS phone
      // property dropdown lists them.
      const phoneHSFields = hubspotFieldsResponse?.data?.results?.filter(
        (f) =>
          (f.fieldType === "phonenumber" || f.fieldType === "text") &&
          f?.modificationMetadata?.readOnlyValue === false
      );
      const phoneChoices = phoneHSFields.map((field) => {
        return {
          value: field.name,
          text: field.label,
        };
      });

      // Dropdown / enumeration properties (fieldType "select") for the
      // PEP / Sanctions / Adverse Media status mappings. These are not in the
      // text/date/phone groups, so filter them explicitly.
      const selectHSFields = hubspotFieldsResponse?.data?.results?.filter(
        (f) =>
          f.fieldType === "select" &&
          f?.modificationMetadata?.readOnlyValue === false
      );
      const selectChoices = selectHSFields.map((field) => {
        return {
          value: field.name,
          text: field.label,
        };
      });

      setTextFields(textChoices);
      setDateFields(dateChoices);
      setPhoneFields(phoneChoices);
      setSelectFields(selectChoices);
    }
  }, [hubspotFieldsResponse]);

  function setExistingReminderSettings() {
    if (
      exstingSettingsResponse.loading === false &&
      exstingSettingsResponse.data !== null
    ) {
      const settings = exstingSettingsResponse?.data?.[0];
      survey.setValue("enabled", settings?.enabled);
      survey.setValue("tranche2_enabled", settings?.data?.tranche2?.enabled === true);
      survey.setValue("first_name", settings?.data?.first_name);
      survey.setValue("last_name", settings?.data?.last_name);
      survey.setValue("document_type", settings?.data?.document_type);
      survey.setValue("document_number", settings?.data?.document_number);
      survey.setValue("document_country", settings?.data?.document_country);
      survey.setValue("date_of_birth", settings?.data?.date_of_birth);
      survey.setValue("valid_from", settings?.data?.valid_from);
      survey.setValue("valid_until", settings?.data?.valid_until);
      survey.setValue("city", settings?.data?.city);
      survey.setValue("state", settings?.data?.state);
      survey.setValue("unit", settings?.data?.unit);
      survey.setValue("street", settings?.data?.street);
      survey.setValue("country", settings?.data?.country);
      survey.setValue("postcode", settings?.data?.postcode);
      survey.setValue("houseNumber", settings?.data?.houseNumber);
      survey.setValue("fullAddress", settings?.data?.fullAddress);
      survey.setValue("idNumber", settings?.data?.idNumber);
      survey.setValue("kyc_check_date", settings?.data?.kyc_check_date);
      survey.setValue("pep_status", settings?.data?.pep_status);
      survey.setValue("sanctions_status", settings?.data?.sanctions_status);
      survey.setValue(
        "adverse_media_status",
        settings?.data?.adverse_media_status
      );
    }

    // The phone property and the client-send suppression flag live in
    // account.sanctions_settings, not the writeback blob — pre-populate them
    // from there. tagbox values are arrays.
    if (accountResponse.loading === false && accountResponse.data !== null) {
      const sanctionsSettings = accountResponse?.data?.sanctions_settings;
      const phoneProperty = sanctionsSettings?.hubspot_phone_property;
      survey.setValue("hubspot_phone_property", phoneProperty ? [phoneProperty] : []);
      survey.setValue(
        "suppress_hubspot_client_sends",
        sanctionsSettings?.suppress_hubspot_client_sends === true
      );
    }
  }

  // surveyjs json
  const json = {
    completedHtml: "<h3>Your changes have been saved.</h3>",
    showQuestionNumbers: "off",
    pages: [
      {
        name: "Write back to HubSpot",
        elements: [
          {
            type: "html",
            name: "question3",
            html: "<strong>Each of the Fields captured during the IdentityCheck process can be update back to HubSpot Contact</strong>\n<br />\n<br />\nSelect the Contact Property and IdentityCheck will write back to HubSpot CRM (if captured)",
          },
          {
            type: "boolean",
            name: "enabled",
            title: "Enabled",
            isRequired: true,
            defaultValue: false,
          },
          {
            type: "boolean",
            name: "tranche2_enabled",
            title: "Enable Tranche 2 (AML) triggers & writebacks",
            description: "On connect, the Tranche 2 contact properties (incl. the 'AML Command' Start/Terminate trigger) are created in HubSpot. Set AML Command = Start to trigger a Tranche 2 verification; results are written back to the contact. KYC Command is unchanged.",
            defaultValue: false,
          },
          {
            type: "boolean",
            name: "suppress_hubspot_client_sends",
            title: "Don't send verification links to clients",
            description:
              "When on, a KYC Command or AML Command of 'Start' still creates the verification, but IdentityCheck sends the client nothing — no verification email, no SMS and no reminders. The link is posted as a note on the HubSpot contact instead, for you to pass on however you like. Affects HubSpot-triggered verifications only.",
            defaultValue: false,
          },
          {
            type: "tagbox",
            name: "hubspot_phone_property",
            title: "Mobile / Phone property for SMS verification",
            description: "Which HubSpot contact property holds the mobile number used for SMS verification links. Leave blank to use HubSpot's standard 'Phone' property (the default). If the selected property is empty on a contact, we fall back to 'Phone'.",
            choices: phoneFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "first_name",
            maxSelectedChoices: 1,
            title: "First Name",
            choices: textFields,
          },
          {
            type: "tagbox",
            name: "last_name",
            title: "Last Name",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "document_type",
            title: "Document Type",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "document_number",
            title: "Document Number",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "document_country",
            title: "Document Country",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "valid_from",
            title: "Valid From",
            choices: dateFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "valid_until",
            title: "Valid Until",
            choices: dateFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            title: "Date of Birth",
            name: "date_of_birth",
            choices: dateFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "city",
            title: "City",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "state",
            title: "State",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "unit",
            title: "Unit",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "street",
            title: "Street",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "country",
            title: "Country",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "postcode",
            title: "Postcode",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "houseNumber",
            title: "House Number",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "fullAddress",
            title: "Full Address",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "idNumber",
            title: "ID Number",
            choices: textFields,
            maxSelectedChoices: 1,
          },
          {
            type: "html",
            name: "aml_writeback_heading",
            html: "<br /><strong>AML check results</strong>\n<br />\nMap the completion date and the PEP / Sanctions / Adverse Media check statuses to HubSpot contact properties. Create each status property in HubSpot as a <strong>dropdown</strong> whose option <em>internal values</em> are exactly <code>possible_matches</code> and <code>no_matches</code>. When a check did not run, the property is left blank.",
          },
          {
            type: "tagbox",
            name: "kyc_check_date",
            title: "KYC Check Date",
            description:
              "HubSpot date property set to the verification completion date.",
            choices: dateFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "pep_status",
            title: "PEP Check Status",
            description:
              "Dropdown property; written as possible_matches / no_matches.",
            choices: selectFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "sanctions_status",
            title: "Sanctions Check Status",
            description:
              "Dropdown property; written as possible_matches / no_matches.",
            choices: selectFields,
            maxSelectedChoices: 1,
          },
          {
            type: "tagbox",
            name: "adverse_media_status",
            title: "Adverse Media Check Status",
            description:
              "Dropdown property; written as possible_matches / no_matches.",
            choices: selectFields,
            maxSelectedChoices: 1,
          }
        ],
      },
    ],
  };

  json.navigateToUrl = navigateToUrl;

  async function saveSurveyResults(url, json) {
    console.log("saveSettings", url, json);
    const response = await Axios.post(url, json);
  }

  const writeBackSettingsComplete = useCallback((sender) => {
    // feature 007: nest the flat T2 toggle into the tranche2 block.
    const data = { ...sender.data, tranche2: { enabled: sender.data.tranche2_enabled === true } };
    delete data.tranche2_enabled;

    // The phone property and the client-send suppression flag belong in
    // account.sanctions_settings, not the writeback blob. Split them out and
    // persist via the existing sanctions settings endpoint. tagbox values are
    // arrays; store the scalar (or "" to clear back to default).
    //
    // Deleting them from `data` is load-bearing: executeWriteBackToHubspot
    // iterates every key of hubspot_writeback.data and indexes each value as a
    // tagbox array, so a stray scalar in that blob is the wrong shape. The
    // server reads the flag with a strict === true, so send a real boolean.
    const rawPhoneProperty = Array.isArray(sender.data.hubspot_phone_property)
      ? sender.data.hubspot_phone_property[0]
      : sender.data.hubspot_phone_property;
    delete data.hubspot_phone_property;
    delete data.suppress_hubspot_client_sends;
    Axios.post("/api/account/sanctions/select", {
      hubspot_phone_property: rawPhoneProperty || "",
      suppress_hubspot_client_sends:
        sender.data.suppress_hubspot_client_sends === true,
    });

    saveSurveyResults("/api/integration/hubspot/internal/settings", data);
  });

  // if hubspot is not connected - return to integrations page
  if (hubspotConnected.loading === false && hubspotConnected.data === false) {
    window.location = "/account/integrations";
  }

  // Don't render a form we haven't hydrated. useAPI never clears `loading` when
  // a request throws, so on a failed fetch every question would sit at its
  // defaultValue and the next save would write those defaults over real stored
  // settings — silently switching suppression back off for a firm that opted
  // out of contacting its clients. Same Loader guard as the sanctions view.
  if (!accountResponse.data || !exstingSettingsResponse.data) {
    return <Loader />;
  }

  const survey = new Model(json);
  applyStackgoTheme(survey);
  survey.onComplete.add(writeBackSettingsComplete);

  setExistingReminderSettings();

  return (
    <Fragment>
      <Survey model={survey} />
    </Fragment>
  );
}
